import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import { Logo } from "@/pages/LandingPage";
import WhatsAppButton, { MobileQuickBar } from "@/components/WhatsAppButton";
import Seo from "@/components/Seo";

export const BLOG_POSTS = [
  {
    slug: "virtual-office-pune",
    title: "Virtual Office in Pune: A Business Address in Maharashtra's Growth Hub",
    excerpt:
      "Pune's booming IT and manufacturing corridors make it a smart registered base. Get a Pune address for GST and clients without leasing office space.",
    date: "2026-11-02",
    readTime: "5 min read",
    category: "City Guides",
    body: [
      "Pune has grown into one of India's strongest business cities — IT parks, manufacturing belts and a huge services economy. A registered Pune address tells clients and partners in Maharashtra that your business is serious and local.",
      "For most growing businesses, though, a physical Pune office is an unnecessary fixed cost. A virtual office gives you the address, mail handling and registration paperwork while your team works from anywhere.",
      "If you need GST registration in Maharashtra, your virtual office address serves as the principal place of business. We prepare the complete document set — rent agreement, No Objection Certificate and utility bill — so your application isn't held up by documentation issues.",
      "A Pune virtual office is equally useful for companies incorporating in Maharashtra: the registered office address required by the MCA is covered, with the supporting paperwork included.",
      "To begin, share your PAN, Aadhaar and business details. Your Pune address with GST-ready documents can be prepared within a few working days.",
    ],
  },
  {
    slug: "virtual-office-hyderabad",
    title: "Virtual Office in Hyderabad: Register Your Business in the City of Growth",
    excerpt:
      "Hyderabad's pharma, IT and startup ecosystem rewards a local address. Here's how to get GST-ready in Hyderabad without renting physical space.",
    date: "2026-11-02",
    readTime: "5 min read",
    category: "City Guides",
    body: [
      "Hyderabad is one of India's fastest-growing business destinations — home to global IT campuses, pharma giants and a thriving startup scene. A Hyderabad address gives your business instant local credibility in Telangana.",
      "A virtual office lets you claim that credibility without a lease. Your invoices, website, GST certificate and MCA records all carry a prime Hyderabad address, while you operate from wherever suits you.",
      "For GST registration in Telangana, the virtual office becomes your principal place of business, backed by the document set we prepare: rent agreement, NOC from the property owner and a recent utility bill.",
      "It's also the practical choice for e-commerce sellers and service companies expanding into South India — you get a compliant Telangana address without opening a physical branch.",
      "Getting started needs only your PAN, Aadhaar and basic business details. The complete GST-ready document set is typically prepared within a few working days.",
    ],
  },
  {
    slug: "virtual-office-mumbai",
    title: "Virtual Office in Mumbai: India's Financial Capital as Your Business Address",
    excerpt:
      "A Mumbai address is the strongest credibility signal in Indian business. Get one for GST, banking and clients — without Mumbai's office rents.",
    date: "2026-11-02",
    readTime: "6 min read",
    category: "City Guides",
    body: [
      "Mumbai is India's financial capital, and a Mumbai business address is arguably the strongest credibility signal your company can send — to clients, banks, marketplaces and partners across the country.",
      "It's also India's most expensive office market, which is exactly why a virtual office makes sense here: you get the Mumbai address for invoices, GST registration, MCA filings and correspondence, without paying Mumbai rents.",
      "For GST purposes in Maharashtra, your virtual office serves as the principal place of business. We provide the rent agreement, NOC and utility bill that the registration process requires, prepared correctly the first time.",
      "Businesses that benefit most: consultants and agencies serving Mumbai clients remotely, e-commerce sellers needing a Maharashtra GST registration, and companies incorporating with a Mumbai registered office.",
      "Start with your PAN, Aadhaar and business details — your Mumbai address with a complete, GST-ready document set can be ready within a few working days.",
    ],
  },
  {
    slug: "virtual-office-kolkata",
    title: "Virtual Office in Kolkata: Prime Business Address Without the Rent",
    excerpt:
      "Kolkata's commercial addresses carry real weight with clients and GST officers alike. Here's how to get one for your business without leasing physical space.",
    date: "2026-11-01",
    readTime: "5 min read",
    category: "City Guides",
    body: [
      "Kolkata remains one of India's most important commercial centres — a registered address here signals an established, serious business to clients across East India and beyond. But office rents in prime commercial areas are out of reach for most early-stage businesses.",
      "A virtual office solves this. You get a prime Kolkata address for your website, invoices, visiting cards and GST certificate, while you work from anywhere — home, a café, or another city entirely.",
      "For GST registration in West Bengal, you need a principal place of business within the state. A virtual office address fulfils this requirement, provided you have the supporting documents: a rent agreement, a No Objection Certificate from the property owner and a utility bill. SolveDesk prepares this complete set for you.",
      "This works especially well for e-commerce sellers expanding into West Bengal, freelancers who want a credible address for client contracts, and companies registering a new entity in Kolkata without relocating a team.",
      "To get started, share your basic documents — PAN, Aadhaar and your business details — and your Kolkata address with GST-ready documentation can be ready within a few working days.",
    ],
  },
  {
    slug: "virtual-office-delhi",
    title: "Virtual Office in Delhi: NCR Presence for GST and Company Registration",
    excerpt:
      "A Delhi address opens doors — with enterprise clients, marketplaces and government registrations. Here's what a virtual office in Delhi gets you and how to set one up.",
    date: "2026-10-28",
    readTime: "5 min read",
    category: "City Guides",
    body: [
      "Delhi NCR is India's largest business market. Whether you're selling to enterprise clients, listing on marketplaces that ask for a Delhi address, or registering a new company, a Delhi business address carries immediate credibility.",
      "Leasing physical office space in Delhi's commercial districts is expensive and locks you into long commitments. A virtual office gives you the same address benefits — business correspondence, GST registration, MCA filings — at a fraction of the cost.",
      "If you're expanding into Delhi for GST purposes, the process is straightforward: declare the virtual office as your principal or additional place of business, attach the rent agreement, NOC and utility bill we provide, and file on the GST portal.",
      "A Delhi virtual office is equally useful for company registration. The MCA requires a registered office address for incorporation — your virtual office address works, with the documentation to prove it.",
      "Share your PAN, Aadhaar and business details with us, and your Delhi address with a complete, GST-ready document set can be prepared within a few working days.",
    ],
  },
  {
    slug: "virtual-office-bangalore",
    title: "Virtual Office in Bangalore: A Startup Address in India's Tech Capital",
    excerpt:
      "Bangalore is where India's startups are judged. Get a credible Bangalore address for your startup — for GST, clients and investors — without paying tech-park rents.",
    date: "2026-10-25",
    readTime: "5 min read",
    category: "City Guides",
    body: [
      "Bangalore is India's startup capital, and a Bangalore address still carries outsized weight — with clients who assume tech businesses are based there, with vendors, and with investors who expect a registered presence in the city.",
      "But most early-stage teams work remotely and don't need physical desks yet. A virtual office gives you a prime Bangalore address for your website, invoices, GST certificate and MCA records, while your team works from wherever it actually is.",
      "For GST registration in Karnataka, your virtual office address serves as the principal place of business. We provide the full document set — rent agreement, NOC from the property owner and a utility bill — so your application goes through without document rejections.",
      "This is also the fastest route for startups incorporating in Karnataka: the registered office address requirement for MCA filings is met by your virtual office, with paperwork to back it.",
      "Getting started takes a handful of documents — PAN, Aadhaar, a photograph and your business details. Your Bangalore address with GST-ready documentation can be ready within a few working days.",
    ],
  },
  {
    slug: "what-is-virtual-office-india",
    title: "What Is a Virtual Office? A Complete Guide for Indian Businesses",
    excerpt:
      "A virtual office gives your business a premium registered address, mail handling and GST-ready documents — without renting physical space. Here's how it works in India.",
    date: "2026-10-20",
    readTime: "6 min read",
    category: "Virtual Office Basics",
    body: [
      "A virtual office is a service that provides your business with a real commercial address in a prime location — without you ever needing to rent or occupy that space physically. In India, this has become the go-to solution for startups, freelancers, e-commerce sellers and remote-first companies that need a professional presence in metro cities.",
      "With a virtual office, you get a registered business address you can use on your invoices, website, visiting cards and GST certificate. Most providers also handle your official mail, forward it to you, and provide the documents needed for government registrations.",
      "The biggest advantage? Cost. A physical office in a metro city can cost lakhs per month. A virtual office gives you the same address credibility for a fraction of that — letting you invest your money in growing the business instead of paying rent.",
      "Whether you're registering a new company, expanding to a new state for GST, or simply want to look established to clients — a virtual office is the fastest, most affordable way to get there.",
    ],
  },
  {
    slug: "virtual-office-gst-registration",
    title: "How to Get GST Registration with a Virtual Office Address",
    excerpt:
      "GST registration requires a valid principal place of business. A virtual office address is legally accepted for GST — here's the step-by-step process and documents you'll need.",
    date: "2026-10-18",
    readTime: "7 min read",
    category: "GST & Compliance",
    body: [
      "Yes — a virtual office address is legally valid for GST registration in India. The GST law requires you to declare a 'principal place of business', and a virtual office address fulfils this requirement as long as you have the right documentation.",
      "Here's how the process works: First, choose a virtual office provider that offers GST-compliant addresses in your target state. Second, collect the required documents — a rent agreement or address proof from the provider, a No Objection Certificate (NOC) from the property owner, and a recent utility bill of the premises.",
      "Third, file your GST application (REG-01) on the GST portal, upload these documents along with your PAN, Aadhaar and photograph, and select the virtual office address as your principal place of business.",
      "The entire process typically takes 3-7 working days if your documents are in order. With a provider like Solve Desk, the document set is prepared for you — so you don't have to chase landlords or worry about rejections.",
    ],
  },
  {
    slug: "documents-required-virtual-office",
    title: "Documents Required for Virtual Office Registration in India",
    excerpt:
      "Whether you're a sole proprietor, LLP or private limited company — here's the complete checklist of documents you need to get a virtual office address in India.",
    date: "2026-10-15",
    readTime: "5 min read",
    category: "Documentation",
    body: [
      "Getting a virtual office in India is straightforward, but having your documents ready speeds things up significantly. Here's what you'll typically need:",
      "For individuals and sole proprietors: PAN card of the business owner, Aadhaar card or any government-issued ID proof, and a passport-size photograph.",
      "For registered entities (LLP, Private Limited, Partnership): Certificate of Incorporation or Partnership Deed, PAN card of the entity, and identity proof of the authorised signatory or director.",
      "Additionally, you'll need to specify your business name, entity type and the proposed use of the virtual office address. Some providers may also ask for a brief description of your business activity.",
      "Once you submit these, the provider prepares your address documents — typically a rent agreement, NOC from the property owner, and a utility bill — which you can then use for GST, MCA or bank account purposes.",
    ],
  },
  {
    slug: "virtual-office-vs-physical-office",
    title: "Virtual Office vs Physical Office: Which One Does Your Business Need?",
    excerpt:
      "Comparing cost, flexibility, compliance and credibility — a practical breakdown to help you decide whether a virtual or physical office makes sense for your stage.",
    date: "2026-10-10",
    readTime: "5 min read",
    category: "Business Strategy",
    body: [
      "The choice between a virtual and physical office isn't about which is 'better' — it's about what your business needs right now.",
      "A physical office makes sense when you have a team that works together daily, when clients visit regularly, or when your operations require dedicated space. But it comes with significant costs: rent, deposits, maintenance, utilities and long-term lease commitments.",
      "A virtual office, on the other hand, gives you everything you need for registration, compliance and professional credibility — at a fraction of the cost. You get a premium address, mail handling, GST documents and the freedom to work from anywhere.",
      "Many businesses start with a virtual office and graduate to physical space as they grow. With Solve Desk's upcoming flexible workspace memberships, you won't even need to switch providers — just upgrade when you're ready.",
    ],
  },
];

export function formatDate(d) {
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function BlogPage() {
  useEffect(() => {
    document.title = "SolveDesk Journal — Virtual Office Guides for India";
  }, []);
  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0" data-testid="blog-page">
      <Seo
        title="SolveDesk Journal — Virtual Office Guides for India"
        description="Practical guides on virtual offices, GST registration, documentation and building your business presence across India."
        path="/blog"
      />
      <WhatsAppButton />
      <MobileQuickBar />
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="blog-logo-link"><Logo /></Link>
          <Link to="/" data-testid="blog-back-link"
            className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200 flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Solve Desk Journal</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-slate-900" data-testid="blog-headline">
            Insights for modern Indian businesses
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-xl">
            Practical guides on virtual offices, GST registration, compliance and building your business presence across India.
          </p>
        </motion.div>

        <div className="mt-12 space-y-6" data-testid="blog-list">
          {BLOG_POSTS.map((post, i) => (
            <motion.article key={post.slug}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_2px_8px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-shadow duration-300"
              data-testid={`blog-card-${post.slug}`}>
              <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground mb-4">
                <span className="rounded-full bg-accent px-3 py-1 text-accent-foreground font-semibold">{post.category}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(post.date)}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
              </div>
              <h2 className="font-display text-2xl font-semibold text-slate-900 group-hover:text-primary transition-colors duration-200">
                {post.title}
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">{post.excerpt}</p>
              <Link to={`/blog/${post.slug}`} data-testid={`blog-read-${post.slug}`}
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all duration-200">
                Read Article <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.article>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo />
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} solvedesk.space</p>
        </div>
      </footer>
    </div>
  );
}
